import React, { useState, useRef, useEffect } from 'react';
import {
  Send,
  Bot,
  User,
  Image as ImageIcon,
  Sparkles,
  LayoutTemplate,
  Settings,
  Eye,
  Plus,
  Copy,
  Share2,
  FileText,
  Palette,
  Check,
  Search,
  Zap,
  Briefcase,
  Folder,
  Coins,
  ShieldCheck,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
} from 'lucide-react';
import { Link } from 'react-router-dom';

type RightPanelTab = 'chat' | 'preview';
type SidebarMode = 'projects' | 'library';

interface Message {
  id: string;
  role: 'user' | 'agent';
  content: React.ReactNode;
  agentName?: string;
  agentRole?: string;
  agentBio?: string;
  agentAvatar?: string;
}

interface DraftEntry {
  id: string;
  emoji: string;
  title: string;
  meta: string;
  artifactCount: number;
  active?: boolean;
  status?: 'running' | 'waiting' | 'idle';
  group: 'TODAY' | 'YESTERDAY' | 'EARLIER';
}

const mockDrafts: DraftEntry[] = [
  { id: 'd1', emoji: '📝', title: 'Cursor 效率革命', meta: '2 min ago · 3 artifacts', artifactCount: 3, active: true, status: 'running', group: 'TODAY' },
  { id: 'd2', emoji: '✨', title: 'AI 资讯日更', meta: '1 h ago · 2 artifacts', artifactCount: 2, status: 'waiting', group: 'TODAY' },
  { id: 'd3', emoji: '📈', title: '小红书爆款拆解', meta: '12/15 · 5 artifacts', artifactCount: 5, group: 'YESTERDAY' },
  { id: 'd4', emoji: '🎨', title: '品牌视觉日志', meta: '12/12 · 4 artifacts', artifactCount: 4, group: 'EARLIER' },
];

interface ArtifactTab {
  id: string;
  title: string;
  active?: boolean;
  hasUnread?: boolean;
}

const mockArtifactTabs: ArtifactTab[] = [
  { id: 'a1', title: 'FlowMore 一站式 AI 内容生产平台', active: true },
  { id: 'a2', title: '配套封面图', hasUnread: true },
  { id: 'a3', title: '行业资料速览' },
];

const mockMessages: Message[] = [
  {
    id: '1',
    role: 'user',
    content: '帮我润色一下这篇关于 FlowMore 的产品介绍，语气要专业且有干货，突出核心卖点。',
  },
  {
    id: '2',
    role: 'agent',
    agentName: 'Vivian',
    agentRole: 'EDITOR',
    agentBio: '擅长结构梳理与文案润色',
    agentAvatar: 'https://api.dicebear.com/7.x/notionists/svg?seed=Vivian&backgroundColor=ffdfbf',
    content: (
      <div className="space-y-3">
        <p>好的，主编！我已经为您重新梳理了文章结构，并优化了段落排版，重点突出「Agentic Workflow」与「一站式分发」。</p>
        <div className="bg-stone-50/70 border border-stone-200 rounded-lg p-3 text-xs">
          <div className="flex items-center gap-2 mb-2 text-stone-900 font-semibold">
            <Sparkles size={13} className="text-amber-500" />
            <span>智能润色卡片</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-stone-500">已优化 12 处文本</span>
            <button className="text-white bg-stone-900 px-2 py-1 rounded hover:bg-black">
              应用到编辑器
            </button>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: '3',
    role: 'user',
    content: '排版有点素，帮忙生成几个带配图的公众号排版预览吧。',
  },
  {
    id: '4',
    role: 'agent',
    agentName: 'Co Co',
    agentRole: 'ART DIRECTOR',
    agentBio: '负责封面与版式设计',
    agentAvatar: 'https://api.dicebear.com/7.x/notionists/svg?seed=Secretary',
    content: (
      <div className="space-y-3">
        <p>排版已生成，搭配科技风封面图，可在右上角切换 Preview 面板查看手机端最终效果，并支持一键切换主题。</p>
        <div className="bg-stone-50/70 border border-stone-200 rounded-lg p-3 text-xs">
          <div className="flex items-center gap-2 mb-2 text-stone-900 font-semibold">
            <LayoutTemplate size={13} className="text-emerald-500" />
            <span>排版预览卡片</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-stone-500">已生成 3 套专属模板</span>
            <button className="text-white bg-stone-900 px-2 py-1 rounded hover:bg-black">
              查看预览
            </button>
          </div>
        </div>
      </div>
    ),
  },
];

export function InteractiveHeroMockup() {
  const [activeTab, setActiveTab] = useState<RightPanelTab>('chat');
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>('projects');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeDraftId, setActiveDraftId] = useState<string>('d1');
  const [activeArtifactId, setActiveArtifactId] = useState<string>('a1');
  const [autoPilot, setAutoPilot] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [localMessages, setLocalMessages] = useState<Message[]>([...mockMessages]);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  };

  useEffect(() => {
    if (activeTab === 'chat') {
      scrollToBottom();
    }
  }, [localMessages, activeTab]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputValue,
    };

    const agentMsg: Message = {
      id: (Date.now() + 1).toString(),
      role: 'agent',
      agentName: '小孟',
      agentRole: 'PUBLISHER',
      agentBio: '负责多平台分发',
      agentAvatar: 'https://api.dicebear.com/7.x/notionists/svg?seed=Publisher&backgroundColor=cbffbd',
      content: (
        <span>
          这是一个演示预览，
          <Link to="/app" className="text-blue-600 hover:text-blue-800 underline font-medium">
            请前往工作区使用
          </Link>
          完整功能。
        </span>
      ),
    };

    setLocalMessages(prev => [...prev, userMsg, agentMsg]);
    setInputValue('');
  };

  const groupedDrafts = (['TODAY', 'YESTERDAY', 'EARLIER'] as const).map((group) => ({
    group,
    items: mockDrafts.filter((d) => d.group === group).map((d) => ({ ...d, active: d.id === activeDraftId })),
  }));

  const projectsCount = mockDrafts.length;
  const filesCount = 24;

  return (
    <div
      data-cursor-element-id="cursor-el-1"
      className="w-full bg-stone-100 rounded-2xl border border-stone-200 shadow-2xl shadow-black/[0.08] overflow-hidden flex flex-col text-left h-[560px] md:h-[720px] font-sans text-stone-900"
    >
      {/* Mac Window Header */}
      <div className="h-10 bg-stone-100 border-b border-stone-200 flex items-center px-4 shrink-0 relative z-40">
        <div className="flex gap-2 absolute left-4">
          <div className="w-3 h-3 rounded-full bg-[#FF5F56] border border-[#E0443E]" />
          <div className="w-3 h-3 rounded-full bg-[#FFBD2E] border border-[#DEA123]" />
          <div className="w-3 h-3 rounded-full bg-[#27C93F] border border-[#1AAB29]" />
        </div>
        <div className="mx-auto text-[11px] font-semibold tracking-wider text-stone-400 flex items-center gap-2 uppercase">
          <span className="text-stone-900">FlowMore</span>
          <span className="text-stone-300">/</span>
          <span>Workspace</span>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden relative">
        {/* ─── Sidebar ─── */}
        <div
          className={`${isSidebarCollapsed ? 'w-14' : 'w-14 md:w-64'} bg-stone-100 flex flex-col h-full flex-shrink-0 z-30 hidden sm:flex transition-all duration-300`}
        >
          {/* Brand & Toggle */}
          <div
            className={`h-12 flex items-center mt-2 mb-2 ${
              isSidebarCollapsed ? 'justify-center' : 'justify-center md:justify-between md:pl-4 md:pr-3'
            }`}
          >
            {isSidebarCollapsed ? (
              <button
                onClick={() => setIsSidebarCollapsed(false)}
                className="p-2 text-stone-500 hover:bg-stone-200 hover:text-stone-900 rounded-md transition-colors"
                title="展开侧边栏"
              >
                <PanelLeftOpen size={16} />
              </button>
            ) : (
              <>
                {/* Mobile: only icon */}
                <div className="md:hidden w-6 h-6 rounded flex items-center justify-center">
                  <img src="/favicon-32x32.png" alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
                </div>
                {/* Desktop: brand + collapse */}
                <div className="hidden md:flex items-center cursor-pointer hover:bg-stone-200/50 rounded-lg transition-colors py-1 px-2 -ml-2">
                  <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0">
                    <img src="/favicon-32x32.png" alt="" aria-hidden="true" className="h-6 w-6 object-contain" />
                  </div>
                  <span className="ml-2 font-medium text-stone-900 text-sm whitespace-nowrap">FlowMore</span>
                </div>
                <button
                  onClick={() => setIsSidebarCollapsed(true)}
                  className="hidden md:inline-flex p-1.5 text-stone-500 hover:bg-stone-200 hover:text-stone-900 rounded-md transition-colors"
                  title="收起侧边栏"
                >
                  <PanelLeftClose size={16} />
                </button>
              </>
            )}
          </div>

          {/* Quick Actions */}
          <div className="px-2 md:px-3 mb-3 space-y-1.5">
            <button
              className={`flex items-center gap-2.5 bg-white border border-stone-200/80 text-stone-700 hover:text-stone-900 hover:shadow-sm w-full py-2 rounded-xl transition-all duration-200 ${
                isSidebarCollapsed ? 'justify-center px-0' : 'justify-center md:justify-start px-0 md:px-3'
              }`}
              title="Create New Project"
            >
              <Plus size={16} className="text-stone-500" />
              {!isSidebarCollapsed && (
                <span className="hidden md:inline text-[13px] font-semibold tracking-wide">New Project</span>
              )}
            </button>
            {!isSidebarCollapsed && (
              <>
                <button
                  className="hidden md:flex items-center gap-2 rounded-lg text-stone-400/80 w-full py-1.5 px-3 cursor-not-allowed"
                  title="敬请期待"
                >
                  <Search size={14} />
                  <span className="text-xs font-medium">Search</span>
                  <span className="ml-auto text-[10px] uppercase tracking-wide">⌘K</span>
                </button>
                <button
                  className="hidden md:flex items-center gap-2 rounded-lg text-stone-400/80 w-full py-1.5 px-3 cursor-not-allowed"
                  title="敬请期待"
                >
                  <Zap size={14} />
                  <span className="text-xs font-medium">Automations</span>
                </button>
              </>
            )}
          </div>

          {/* Mode Tabs (Projects / Files) */}
          <div className={`px-2 md:px-3 mb-3 ${isSidebarCollapsed ? 'mb-2' : ''}`}>
            {isSidebarCollapsed ? (
              <div className="space-y-1">
                <button
                  onClick={() => setSidebarMode('projects')}
                  className={`w-full flex items-center justify-center rounded-lg py-1.5 transition-colors ${
                    sidebarMode === 'projects'
                      ? 'bg-stone-200/75 text-stone-900'
                      : 'text-stone-500 hover:bg-stone-200/45 hover:text-stone-800'
                  }`}
                  title="Projects"
                >
                  <Briefcase size={13} />
                </button>
                <button
                  onClick={() => setSidebarMode('library')}
                  className={`w-full flex items-center justify-center rounded-lg py-1.5 transition-colors ${
                    sidebarMode === 'library'
                      ? 'bg-stone-200/75 text-stone-900'
                      : 'text-stone-500 hover:bg-stone-200/45 hover:text-stone-800'
                  }`}
                  title="Files"
                >
                  <Folder size={13} />
                </button>
              </div>
            ) : (
              <div className="hidden md:grid grid-cols-2 gap-1 rounded-xl border border-stone-200/80 bg-stone-200/40 p-1">
                <button
                  onClick={() => setSidebarMode('projects')}
                  className={`rounded-lg py-1.5 text-xs font-medium transition-colors ${
                    sidebarMode === 'projects' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-800'
                  }`}
                >
                  <span className="inline-flex items-center gap-1">
                    <span>Projects</span>
                    <span className="text-[10px] tabular-nums text-stone-400">{projectsCount}</span>
                  </span>
                </button>
                <button
                  onClick={() => setSidebarMode('library')}
                  className={`rounded-lg py-1.5 text-xs font-medium transition-colors ${
                    sidebarMode === 'library' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-800'
                  }`}
                >
                  <span className="inline-flex items-center gap-1">
                    <span>Files</span>
                    <span className="text-[10px] tabular-nums text-stone-400">{filesCount}</span>
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* Draft List */}
          <div className="flex-1 overflow-y-scroll px-2 custom-scrollbar">
            {sidebarMode === 'projects' ? (
              !isSidebarCollapsed && (
                <div className="hidden md:block space-y-3 pl-1 pr-1">
                  {groupedDrafts.map(({ group, items }) =>
                    items.length === 0 ? null : (
                      <div key={group} className="space-y-1">
                        <div className="px-2 pt-1 pb-0.5 text-[10px] font-semibold text-stone-400/80 uppercase tracking-widest">
                          {group}
                        </div>
                        {items.map((draft) => (
                          <button
                            key={draft.id}
                            onClick={() => setActiveDraftId(draft.id)}
                            className={`w-full flex items-start gap-2 px-2 py-1.5 rounded-lg text-sm text-left transition-colors ${
                              draft.active
                                ? 'bg-white shadow-sm border border-stone-200/80 text-stone-900'
                                : 'text-stone-500 hover:bg-stone-200/40 hover:text-stone-800 border border-transparent'
                            }`}
                            title={draft.title}
                          >
                            <span
                              className={`mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0 ${
                                draft.active ? 'bg-stone-700' : 'bg-stone-300'
                              }`}
                            />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5">
                                <span className="truncate text-[13px] font-medium">{draft.title}</span>
                                {draft.status === 'running' && (
                                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse flex-shrink-0" />
                                )}
                                {draft.status === 'waiting' && (
                                  <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse flex-shrink-0" />
                                )}
                              </div>
                              <div className="text-[11px] text-stone-400 truncate mt-0.5">{draft.meta}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )
                  )}
                </div>
              )
            ) : (
              !isSidebarCollapsed && (
                <div className="hidden md:block px-2 py-1 space-y-1">
                  <div className="flex flex-wrap gap-1 mb-2">
                    {['全部 24', '文章 12', '信息 8', '图片 4'].map((label, i) => (
                      <span
                        key={label}
                        className={`px-2 py-0.5 rounded text-[10px] border ${
                          i === 0
                            ? 'border-stone-400 text-stone-700 bg-stone-100'
                            : 'border-stone-200 text-stone-400'
                        }`}
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                  {[
                    { type: 'folder', label: '产品资料', icon: Folder },
                    { type: 'file', label: 'FlowMore 介绍', icon: FileText },
                    { type: 'file', label: '行业洞察 · 03', icon: FileText },
                    { type: 'image', label: '科技风封面', icon: ImageIcon },
                  ].map((row) => (
                    <button
                      key={row.label}
                      type="button"
                      className="w-full flex items-center gap-1.5 text-left px-2 py-1 rounded text-[11px] text-stone-500 hover:bg-stone-100 hover:text-stone-700"
                    >
                      <row.icon size={12} className="text-stone-400 shrink-0" />
                      <span className="truncate">{row.label}</span>
                    </button>
                  ))}
                </div>
              )
            )}
          </div>

          {/* User Profile Footer */}
          <div className="p-2 md:p-3 mt-auto mb-1">
            <div
              className={`flex items-center rounded-xl bg-[#e5e3e0]/60 hover:bg-[#e5e3e0]/80 transition-all duration-200 ${
                isSidebarCollapsed ? 'justify-center p-2' : 'justify-center md:justify-start gap-2.5 p-2 md:px-3 md:py-2.5'
              }`}
            >
              <div className="w-7 h-7 rounded-full bg-stone-300 overflow-hidden flex-shrink-0 border border-stone-200/50">
                <img
                  src="https://api.dicebear.com/7.x/notionists/svg?seed=FlowEditor"
                  alt="User"
                  className="w-full h-full object-cover opacity-90"
                />
              </div>
              {!isSidebarCollapsed && (
                <>
                  <div className="hidden md:block min-w-[80px] flex-1 overflow-hidden">
                    <p className="text-[13px] font-semibold text-stone-700 truncate leading-tight">Flow Editor</p>
                    <p className="text-[11px] font-medium text-stone-400 truncate flex items-center gap-1 mt-0.5">
                      <Coins size={11} className="text-amber-600/80" />
                      <span className="tabular-nums">12k</span>
                      <span>credits</span>
                    </p>
                  </div>
                  <Settings size={14} className="hidden md:inline text-stone-400 ml-auto" />
                </>
              )}
            </div>
          </div>
        </div>

        {/* ─── Main Content Area ─── */}
        <div className="flex-1 h-full overflow-hidden relative shadow-[-20px_0_40px_rgba(0,0,0,0.03)] bg-white sm:rounded-l-3xl sm:ml-[-1px] flex">
          {/* ── ArtifactViewer Mock ── */}
          <div className="flex-1 flex flex-col bg-white h-full relative overflow-hidden min-w-0">
            {/* Glass Header (artifact tabs) */}
            <div className="bg-white/90 backdrop-blur border-b border-stone-200 sticky top-0 z-20">
              <div className="h-12 px-4 md:px-8 max-w-5xl mx-auto flex items-center justify-between gap-3">
                <div className="flex items-center gap-1 overflow-x-auto no-scrollbar flex-1 min-w-0">
                  {mockArtifactTabs.map((tab) => {
                    const isActive = tab.id === activeArtifactId;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveArtifactId(tab.id)}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[12.5px] transition-all flex-shrink-0 max-w-[200px] ${
                          isActive
                            ? 'bg-stone-100 border border-stone-200/80 shadow-[0_1px_2px_rgba(0,0,0,0.04)] text-stone-900 font-medium'
                            : 'text-stone-500 hover:bg-stone-50 border border-transparent'
                        }`}
                        title={tab.title}
                      >
                        <FileText size={13} className={isActive ? 'text-blue-500' : 'text-stone-400'} />
                        <span className="truncate">{tab.title}</span>
                        {tab.hasUnread && (
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0 ml-2">
                  <button className="p-1.5 rounded-md text-stone-400 hover:text-stone-900 hover:bg-stone-100 relative group/btn">
                    <Plus size={16} />
                    <span className="absolute top-full mt-1.5 right-0 px-2 py-1 bg-stone-800 text-white text-[10px] rounded opacity-0 group-hover/btn:opacity-100 whitespace-nowrap pointer-events-none transition-opacity font-medium z-50">
                      新增稿件
                    </span>
                  </button>
                  <div className="w-[1px] h-4 bg-stone-200 mx-1" />
                  <button className="p-1.5 rounded-md text-stone-400 hover:text-stone-900 hover:bg-stone-100">
                    <Copy size={16} />
                  </button>
                  <button className="p-1.5 rounded-md text-stone-400 hover:text-stone-900 hover:bg-stone-100">
                    <Share2 size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Editor Area */}
            <div className="flex-1 overflow-y-auto px-6 py-8 md:px-12 md:py-10 bg-white">
              <div className="max-w-3xl mx-auto animate-fade-in">
                {/* Cover */}
                <div className="mb-6">
                  <div className="w-full h-40 md:h-48 bg-stone-100 rounded-xl border border-stone-200 flex flex-col items-center justify-center text-stone-400 overflow-hidden relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50 opacity-60" />
                    <ImageIcon size={28} className="relative z-10 mb-1.5 opacity-50 text-indigo-400" />
                    <span className="relative z-10 text-xs font-medium text-indigo-900/50">已由 Agent 自动生成配套封面</span>
                  </div>
                </div>

                {/* Title row with save status */}
                <div className="mb-5 flex items-center justify-between pb-2 gap-3">
                  <h1 className="text-2xl md:text-3xl font-sans font-semibold text-stone-900 truncate flex-1">
                    FlowMore：一站式 AI 内容生产平台
                  </h1>
                  <span className="text-stone-300 flex items-center" title="已保存">
                    <Check size={16} />
                  </span>
                </div>

                {/* Body */}
                <div className="space-y-4 text-[14.5px] md:text-[15.5px] text-stone-600 leading-relaxed">
                  <p>
                    在信息爆炸的时代，内容创作者和运营团队往往被繁琐的流程所困：从选题策划、资料搜集、文章撰写，到多平台适配、排版预览，再到最后的发布分发。FlowMore 致力于打破这些壁垒，为你提供一个全自动化、一站式的内容生产与分发工作台。
                  </p>

                  <h2 className="text-lg md:text-xl font-semibold text-stone-900 mt-6 mb-3">
                    核心理念：你只管定方向，Agent 负责执行
                  </h2>
                  <p>
                    与传统的 AI 写作助手不同，FlowMore 采用基于 Agentic Workflow 的设计。你不再需要一步步引导 AI，而是直接向一支预设好的「专家团队」下达高层指令。
                  </p>

                  <div className="bg-stone-50 p-4 rounded-lg border-l-4 border-stone-900 my-4">
                    <p className="text-stone-900 font-medium text-[14px]">
                      "FlowMore 不是在帮你打字，而是在为你配备一整个无形的编辑部。"
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── RightContextPanel Mock ── */}
          <div className="w-full md:w-[380px] bg-[#FBFBFA] border-l border-stone-100 flex flex-col h-full flex-shrink-0 z-20 shadow-[-10px_0_30px_rgba(0,0,0,0.02)] relative hidden lg:flex">
            {/* Tabs row + collapse */}
            <div className="flex items-center p-1 m-2 gap-1 flex-shrink-0">
              <div className="flex bg-[#EFEFED] rounded-lg border border-[#E3E2E0] flex-1 p-0.5">
                <button
                  onClick={() => setActiveTab('chat')}
                  className={`flex-1 py-1 text-[11.5px] font-semibold rounded-md flex items-center justify-center gap-1.5 transition-all ${
                    activeTab === 'chat' ? 'bg-white text-[#37352F] shadow-sm' : 'text-[#787774] hover:text-[#37352F]'
                  }`}
                >
                  <Bot size={13} /> Assistant
                </button>
                <button
                  onClick={() => setActiveTab('preview')}
                  className={`flex-1 py-1 text-[11.5px] font-semibold rounded-md flex items-center justify-center gap-1.5 transition-all ${
                    activeTab === 'preview' ? 'bg-white text-[#37352F] shadow-sm' : 'text-[#787774] hover:text-[#37352F]'
                  }`}
                >
                  <Eye size={13} /> Preview
                </button>
              </div>
              <button
                className="p-1.5 rounded-md text-stone-500 hover:bg-stone-200 hover:text-stone-900 transition-colors flex-shrink-0"
                title="收起面板"
              >
                <PanelRightClose size={15} />
              </button>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-hidden relative">
              {activeTab === 'chat' ? (
                <AgentRoomMock
                  messages={localMessages}
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                  onSubmit={handleSubmit}
                  autoPilot={autoPilot}
                  setAutoPilot={setAutoPilot}
                  messagesContainerRef={messagesContainerRef}
                  messagesEndRef={messagesEndRef}
                />
              ) : (
                <PreviewPaneMock />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── AgentRoom Mock ─── */

function AgentRoomMock({
  messages,
  inputValue,
  setInputValue,
  onSubmit,
  autoPilot,
  setAutoPilot,
  messagesContainerRef,
  messagesEndRef,
}: {
  messages: Message[];
  inputValue: string;
  setInputValue: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  autoPilot: boolean;
  setAutoPilot: (v: boolean) => void;
  messagesContainerRef: React.RefObject<HTMLDivElement>;
  messagesEndRef: React.RefObject<HTMLDivElement>;
}) {
  const lastAgent = [...messages].reverse().find((m) => m.role === 'agent');
  const progress = 65;
  const ringRadius = 16;
  const ringCircumference = 2 * Math.PI * ringRadius;
  const ringOffset = ringCircumference * (1 - progress / 100);

  return (
    <div className="flex flex-col h-full bg-[#F7F7F5]">
      {/* AgentStage */}
      <div className="bg-white border-b border-stone-200 px-4 py-3 flex-shrink-0">
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-start gap-2.5 min-w-0">
            <div className="relative flex-shrink-0">
              {lastAgent?.agentAvatar ? (
                <img
                  src={lastAgent.agentAvatar}
                  alt={lastAgent.agentName}
                  className="w-9 h-9 rounded-xl border border-stone-200 object-cover bg-stone-100"
                />
              ) : (
                <div className="w-9 h-9 rounded-xl border border-stone-200 bg-stone-100 flex items-center justify-center">
                  <Bot size={18} className="text-stone-400" />
                </div>
              )}
              <div className="absolute -bottom-0.5 -right-0.5 bg-green-500 w-2.5 h-2.5 rounded-full border-2 border-white animate-pulse" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-stone-900 text-[13px] truncate">
                {lastAgent?.agentName || 'Vivian'}
              </h3>
              <p className="text-[10px] text-stone-400 uppercase tracking-wide font-medium truncate">
                {lastAgent?.agentRole || 'EDITOR'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="relative h-9 w-9">
              <svg className="-rotate-90 h-9 w-9" viewBox="0 0 40 40" aria-hidden>
                <circle cx="20" cy="20" r={ringRadius} fill="none" stroke="rgba(231,229,228,0.95)" strokeWidth="3.5" />
                <circle
                  cx="20"
                  cy="20"
                  r={ringRadius}
                  fill="none"
                  stroke="#1c1917"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeDasharray={ringCircumference}
                  strokeDashoffset={ringOffset}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center text-[9px] font-semibold text-stone-700 tabular-nums">
                {progress}%
              </div>
            </div>
            <button
              onClick={() => setAutoPilot(!autoPilot)}
              className={`flex items-center gap-1 px-1.5 py-1 rounded-md border transition-all ${
                autoPilot
                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                  : 'bg-white border-stone-200 text-stone-500 hover:border-stone-300 hover:text-stone-900'
              }`}
              title={autoPilot ? 'Auto-Pilot ON' : 'Auto-Pilot OFF'}
            >
              {autoPilot ? (
                <Zap size={11} className="fill-blue-500 text-blue-500" />
              ) : (
                <ShieldCheck size={11} />
              )}
              <span className="text-[9px] font-semibold uppercase tracking-wide">
                {autoPilot ? 'Auto' : 'Check'}
              </span>
            </button>
          </div>
        </div>
        <div className="text-[10px] text-stone-500 flex justify-end">
          <span className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Processing...
          </span>
        </div>
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-3 space-y-4">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start items-start gap-2'}`}
          >
            {msg.role !== 'user' && (
              <div className="w-7 h-7 rounded-lg border border-stone-200 overflow-hidden flex-shrink-0 mt-1 bg-white">
                {msg.agentAvatar ? (
                  <img src={msg.agentAvatar} alt={msg.agentName || 'Agent'} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Bot size={12} className="text-stone-400" />
                  </div>
                )}
              </div>
            )}
            <div className={`max-w-[82%] flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
              <span className="text-[10px] text-stone-400 mb-0.5 px-1">
                {msg.role === 'user' ? 'Me' : msg.agentName || 'Agent'}
              </span>
              {msg.role !== 'user' && msg.agentBio && (
                <span className="text-[9px] text-stone-500 mb-1 px-1 leading-tight">{msg.agentBio}</span>
              )}
              <div
                className={`shadow-sm p-2.5 text-[12.5px] leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-[#37352F] text-white rounded-2xl rounded-tr-none'
                    : 'bg-white text-[#37352F] border border-stone-100 rounded-2xl rounded-tl-none'
                }`}
              >
                {msg.content}
              </div>
            </div>
            {msg.role === 'user' && (
              <div className="w-5 h-5 rounded flex items-center justify-center bg-blue-100 text-blue-700 flex-shrink-0 mt-1 ml-1">
                <User size={11} />
              </div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} className="h-2" />
      </div>

      {/* Composer */}
      <div className="p-3 bg-white border-t border-stone-200 flex-shrink-0">
        <form onSubmit={onSubmit} className="relative rounded-[20px] border border-stone-200 bg-white shadow-sm pl-3 pr-10 py-2 focus-within:ring-1 focus-within:ring-stone-400 transition-all">
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="给 Agent 下指令，@ 引用资料，Enter 发送"
            className="w-full bg-transparent text-[12.5px] leading-6 text-stone-900 placeholder:text-stone-400 resize-none focus:outline-none min-h-[44px]"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSubmit(e as unknown as React.FormEvent);
              }
            }}
          />
          <button
            type="submit"
            disabled={!inputValue.trim()}
            className={`absolute right-2 bottom-2 p-1.5 rounded-full transition-colors shadow-sm ${
              inputValue.trim()
                ? 'bg-stone-900 text-white hover:bg-black'
                : 'bg-stone-200 text-stone-400 cursor-not-allowed'
            }`}
          >
            <Send size={13} className="-ml-0.5" />
          </button>
        </form>
        <div className="mt-1.5 px-1 text-[10px] text-stone-400 flex justify-between">
          <span>Shift + Enter 换行</span>
          <span className="text-stone-500">已引用 1 条资料</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Preview Pane Mock ─── */

function PreviewPaneMock() {
  return (
    <div className="flex-1 flex flex-col h-full bg-[#F2F1EE]">
      {/* Platform & Theme Header */}
      <div className="bg-white border-b border-[#E3E2E0] p-3 flex flex-col gap-3 flex-shrink-0 z-10">
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar w-full">
          <button className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#37352F] text-white shadow-sm">
            <span>微信公众号</span>
          </button>
          <button className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#F7F7F5] text-[#787774]">
            <span>Twitter/X</span>
          </button>
          <button className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#F7F7F5] text-[#787774]">
            <span>小红书</span>
          </button>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-[10px] font-semibold text-[#9D9D9B] uppercase tracking-wider flex items-center gap-1">
            Device View
            <span className="bg-green-100 text-green-700 px-1 rounded text-[9px]">TrueScale</span>
          </span>
          <div className="flex items-center gap-1 px-2 py-1.5 rounded-md bg-[#F7F7F5] text-[#787774]">
            <Palette size={13} />
            <span className="text-[10px] font-semibold">Style: Mono</span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 flex justify-center items-start">
        <div
          className="bg-white shadow-xl overflow-hidden relative flex flex-col flex-shrink-0 rounded-[40px] border-[8px] border-[#37352F] origin-top"
          style={{ width: '375px', height: '700px', transform: 'scale(0.85)' }}
        >
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[120px] h-[25px] bg-[#37352F] rounded-b-[16px] z-20 flex justify-center items-end pb-1">
            <div className="w-10 h-1 bg-[#555] rounded-full opacity-50" />
          </div>
          <div className="flex-1 overflow-y-auto pt-10 pb-10 px-4">
            <h1 className="text-xl font-semibold mb-1 leading-tight text-[#333]">
              FlowMore：一站式 AI 内容生产平台
            </h1>
            <div className="text-[10px] mb-4 flex gap-2 text-[#999]">
              <span>FlowMore</span>
              <span className="text-[#576b95]">Today</span>
            </div>
            <div className="w-full mb-3 rounded overflow-hidden bg-stone-100 h-40 flex items-center justify-center relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50 opacity-60" />
              <ImageIcon size={24} className="text-indigo-400 opacity-50 relative z-10" />
            </div>
            <div className="leading-7 text-justify text-base">
              <p className="mb-4 text-[#333]">
                在信息爆炸的时代，内容创作者和运营团队往往被繁琐的流程所困：从选题策划、资料搜集、文章撰写，到多平台适配、排版预览，再到最后的发布分发。
              </p>
              <blockquote className="border-l-4 border-[#576b95] pl-3 py-1 my-4 bg-[#f7f7f7] text-[#666] text-sm">
                <p className="font-semibold text-[#333] mb-1">💡 核心理念：你只管定方向，Agent 负责执行</p>
                <p>与传统的 AI 写作助手不同，FlowMore 采用基于 Agentic Workflow 的设计。你只需向一支预设好的「专家团队」下达高层指令...</p>
              </blockquote>
              <p className="mb-4 text-[#333]">
                完成项目后，你无需频繁在不同工具之间复制粘贴。FlowMore 提供真实精准的「多平台预览」面板（如微信公众号、Twitter 等），实时确认最终视觉效果。
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
